import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatCurrency';
import './EditOrderModal.css';

function EditOrderModal({ order, isOpen, onClose, onSave }) {
  const { t } = useTranslation();
  const [editedItems, setEditedItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (order?.items) {
      setEditedItems(order.items.map((item) => ({ ...item })));
    }
  }, [order]);

  const handleQuantityChange = useCallback((idx, delta) => {
    setEditedItems((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;
        const newQty = Math.max(0, item.quantity + delta);
        return { ...item, quantity: newQty };
      })
    );
  }, []);

  const handleRemoveItem = useCallback((idx) => {
    setEditedItems((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleSave = async () => {
    const remainingItems = editedItems.filter((item) => item.quantity > 0);
    if (remainingItems.length === 0) return;

    setSaving(true);
    try {
      await onSave(order, remainingItems);
      onClose();
    } catch (error) {
      console.error('Error saving order:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !order) return null;

  const remainingItems = editedItems.filter((item) => item.quantity > 0);
  const hasChanges =
    JSON.stringify(remainingItems.map((i) => ({ id: i.menuItemId || i.name, qty: i.quantity }))) !==
    JSON.stringify(order.items.map((i) => ({ id: i.menuItemId || i.name, qty: i.quantity })));

  return (
    <div className="edit-order-overlay" onClick={onClose}>
      <div className="edit-order-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-order-header">
          <h2>{t('orders.editOrder')}</h2>
          <span className="edit-order-id">#{order.orderId.slice(-6)}</span>
          <button className="edit-order-close" onClick={onClose} aria-label={t('common.close', 'Close')}>
            <span className="material-icons" aria-hidden="true">close</span>
          </button>
        </div>

        <div className="edit-order-items">
          {editedItems.map((item, idx) => {
            if (item.quantity <= 0) return null;
            const modifiers = item.selectedModifiers || item.modifiers || [];
            return (
              <div key={idx} className="edit-order-item">
                <div className="edit-order-item-info">
                  <span className="edit-order-item-name">{item.name}</span>
                  <span className="edit-order-item-price">{formatCurrency(item.price)}</span>
                  {modifiers.length > 0 && (
                    <div className="edit-order-item-modifiers">
                      {modifiers.map((mod, modIdx) => (
                        <span key={modIdx} className="edit-order-item-modifier">
                          + {mod.optionName || mod.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="edit-order-item-controls">
                  <button
                    className="edit-order-qty-btn"
                    onClick={() => handleQuantityChange(idx, -1)}
                    aria-label={t('orders.decreaseQty')}
                  >
                    <span className="material-icons" aria-hidden="true">remove</span>
                  </button>
                  <span className="edit-order-qty">{item.quantity}</span>
                  <button
                    className="edit-order-qty-btn"
                    onClick={() => handleQuantityChange(idx, 1)}
                    aria-label={t('orders.increaseQty')}
                  >
                    <span className="material-icons" aria-hidden="true">add</span>
                  </button>
                  <button
                    className="edit-order-remove-btn"
                    onClick={() => handleRemoveItem(idx)}
                    aria-label={t('orders.removeItem')}
                  >
                    <span className="material-icons" aria-hidden="true">delete</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {remainingItems.length === 0 && (
          <div className="edit-order-empty">
            <span className="material-icons" aria-hidden="true">info</span>
            <span>{t('orders.editNoItems')}</span>
          </div>
        )}

        <div className="edit-order-actions">
          <button className="edit-order-cancel-btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            className="edit-order-save-btn"
            onClick={handleSave}
            disabled={!hasChanges || remainingItems.length === 0 || saving}
          >
            {saving ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditOrderModal;
